/**
 * AssistantDrawer — Norea AI Assistant chat panel.
 *
 * Sliding drawer pinned to the right side of the viewport.
 * Persists conversation in localStorage (per browser, not per backend session).
 * The backend is stateless — we resend last N turns on each request.
 *
 * Actions rendered as clickable buttons next to each assistant reply:
 *   - dossier_card → click navigates to /dossiers/<id>
 *   - navigate     → click navigates to <path>
 *   - prefill_relance → click navigates to dossier + auto-opens WhatsApp wa.me with text
 *   - copy         → click copies payload.text to clipboard
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot, X, Send, Loader2, ExternalLink, Copy as CopyIcon, FileText,
  Sparkles, MessageSquare, Trash2, Mic, Square,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STORAGE_KEY = "norea_assistant_chat_v1";
const MAX_TURNS_KEPT = 30;

const QUICK_PROMPTS = [
  "Quels dossiers à relancer aujourd'hui ?",
  "Combien de dossiers PV incomplets ?",
  "Résume le dernier dossier reçu",
  "Quel partenaire a le plus de retards ?",
];

export default function AssistantDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {/* ignore */}
    return [];
  });
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);
  const scrollRef = useRef(null);

  // Persist history (without action objects' transient state) in localStorage
  useEffect(() => {
    try {
      const compact = messages.slice(-MAX_TURNS_KEPT);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(compact));
    } catch {/* ignore quota errors */}
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending]);

  const send = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || pending) return;
    setInput("");

    const userMsg = { role: "user", content: trimmed, ts: Date.now() };
    const next = [...messages, userMsg];
    setMessages(next);
    setPending(true);

    try {
      // Only send role+content to backend (drop timestamps/actions)
      const history = next.map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post("/assistant/chat", { messages: history });
      const assistantMsg = {
        role: "assistant",
        content: data.reply || "(réponse vide)",
        actions: data.actions || [],
        ts: Date.now(),
      };
      setMessages([...next, assistantMsg]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur côté serveur";
      setMessages([
        ...next,
        { role: "assistant", content: `⚠️ ${detail}`, actions: [], ts: Date.now(), error: true },
      ]);
    } finally {
      setPending(false);
    }
  };

  const handleAction = async (action) => {
    if (action.type === "dossier_card" && action.dossier_id) {
      navigate(`/dossiers/${action.dossier_id}`);
      onClose?.();
      return;
    }
    if (action.type === "navigate" && action.path) {
      navigate(action.path);
      onClose?.();
      return;
    }
    if (action.type === "copy" && action.payload?.text) {
      try {
        await navigator.clipboard.writeText(action.payload.text);
        toast.success("📋 Copié dans le presse-papier", { duration: 1500 });
      } catch {
        toast.error("Impossible de copier");
      }
      return;
    }
    if (action.type === "prefill_relance" && action.dossier_id) {
      // Resolve the partner's WhatsApp number via the existing meta endpoint
      try {
        const { data } = await api.get("/dossiers/_meta/whatsapp-templates", {
          params: { dossier_id: action.dossier_id },
        });
        const num = (data.partner?.whatsapp || "").trim();
        const text = action.payload?.message || "";
        if (!num) {
          toast.warning("Numéro WhatsApp du partenaire non configuré", {
            description: "Ajoute-le dans /configuration. Le message a été copié en attendant.",
          });
          try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
          navigate(`/dossiers/${action.dossier_id}`);
          onClose?.();
          return;
        }
        const url = `https://wa.me/${encodeURIComponent(num)}?text=${encodeURIComponent(text)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("WhatsApp ouvert avec le message prêt à envoyer", { duration: 2500 });
      } catch {
        toast.error("Impossible de préparer le WhatsApp");
      }
      return;
    }
  };

  const clearHistory = () => {
    if (!window.confirm("Effacer la conversation avec l'assistant ?")) return;
    setMessages([]);
    try { localStorage.removeItem(STORAGE_KEY); } catch {/* ignore */}
  };

  // ---------- Voice input (Whisper) ----------
  const startRecording = async () => {
    if (recording || transcribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingStreamRef.current = stream;
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        // Release the mic
        try { recordingStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {/* ignore */}
        recordingStreamRef.current = null;

        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        audioChunksRef.current = [];
        if (!blob.size) {
          toast.error("Aucun audio capturé");
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/assistant/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (!text) {
            toast.warning("Rien d'audible n'a été détecté");
            return;
          }
          // Append transcribed text to current input (don't overwrite if user already typed something)
          setInput((prev) => (prev ? `${prev} ${text}` : text));
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription impossible");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone inaccessible — vérifie les permissions du navigateur");
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") mr.stop();
    setRecording(false);
  };

  // Stop recording when drawer closes (avoid keeping mic open in background)
  useEffect(() => {
    if (!open && recording) stopRecording();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-norea-text/30 backdrop-blur-[2px] transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        data-testid="assistant-backdrop"
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-[440px] bg-white border-l border-norea-border shadow-2xl flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="assistant-drawer"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-norea-border flex items-center gap-3 bg-gradient-to-br from-norea-primary/5 to-norea-accent/5">
          <div className="w-9 h-9 rounded-full bg-norea-primary/10 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-norea-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-base font-medium text-norea-text">Norea Assistant</div>
            <div className="text-[11px] text-norea-textMuted">
              Recherche, synthèse, relance — par Gemini 2.5
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={clearHistory}
              className="p-1.5 text-norea-textMuted hover:text-status-erreurText hover:bg-norea-bg rounded transition"
              title="Effacer la conversation"
              data-testid="assistant-clear"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-norea-textMuted hover:text-norea-text hover:bg-norea-bg rounded transition"
            data-testid="assistant-close"
            title="Fermer (Échap)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
          data-testid="assistant-messages"
        >
          {messages.length === 0 && (
            <div className="text-center py-12 px-4">
              <div className="w-14 h-14 rounded-full bg-norea-primary/10 mx-auto mb-3 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-norea-primary" />
              </div>
              <div className="text-sm font-medium text-norea-text">Bonjour 👋</div>
              <div className="text-xs text-norea-textMuted mt-1 mb-4">
                Demande-moi ce que tu veux sur tes dossiers. Quelques exemples :
              </div>
              <div className="space-y-1.5">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="block w-full text-left text-xs text-norea-text bg-norea-bg hover:bg-norea-surfaceHover border border-norea-border rounded-md px-3 py-2 transition"
                    data-testid="assistant-prompt-suggestion"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <MessageBubble key={i} message={m} onAction={handleAction} />
          ))}

          {pending && (
            <div className="flex items-center gap-2 text-xs text-norea-textMuted px-3 py-2" data-testid="assistant-thinking">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>Norea Assistant réfléchit…</span>
            </div>
          )}
        </div>

        {/* Input */}
        <form
          className="px-3 py-3 border-t border-norea-border bg-white"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                recording ? "🔴 Enregistrement en cours… clique sur ⏹ pour transcrire"
                : transcribing ? "Transcription en cours…"
                : "Pose ta question… (Entrée pour envoyer)"
              }
              rows={1}
              className="input-field flex-1 text-sm resize-none max-h-32"
              disabled={pending || recording || transcribing}
              data-testid="assistant-input"
            />
            <button
              type="button"
              onClick={recording ? stopRecording : startRecording}
              disabled={pending || transcribing}
              className={`p-2 shrink-0 rounded-md border transition ${
                recording
                  ? "bg-status-erreurBg border-status-erreurBorder text-status-erreurText animate-pulse"
                  : transcribing
                  ? "bg-norea-bg border-norea-border text-norea-textMuted"
                  : "bg-white border-norea-border text-norea-text hover:bg-norea-bg hover:border-norea-primary/40 hover:text-norea-primary"
              }`}
              data-testid="assistant-mic"
              title={recording ? "Arrêter l'enregistrement" : transcribing ? "Transcription…" : "Dicter avec le micro"}
              aria-label={recording ? "Arrêter le micro" : "Dicter avec le micro"}
            >
              {transcribing ? <Loader2 className="w-4 h-4 animate-spin" />
                : recording ? <Square className="w-4 h-4 fill-current" />
                : <Mic className="w-4 h-4" />}
            </button>
            <button
              type="submit"
              disabled={pending || recording || transcribing || !input.trim()}
              className="btn-primary p-2 shrink-0"
              data-testid="assistant-send"
              aria-label="Envoyer"
            >
              {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          <div className="text-[10px] text-norea-textMuted mt-2 px-1">
            ⚡ Les réponses sont basées sur les 60 dossiers les plus récents. L'assistant ne supprime ni n'archive rien sans ton clic.
          </div>
        </form>
      </aside>
    </>
  );
}

function MessageBubble({ message, onAction }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${message.role}`}>
      <div className={`max-w-[90%] ${isUser ? "" : "w-full"}`}>
        <div
          className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap leading-relaxed ${
            isUser
              ? "bg-norea-primary text-white rounded-br-sm"
              : message.error
              ? "bg-status-erreurBg border border-status-erreurBorder text-status-erreurText rounded-bl-sm"
              : "bg-norea-bg border border-norea-border text-norea-text rounded-bl-sm"
          }`}
        >
          {message.content}
        </div>
        {!isUser && (message.actions || []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="msg-actions">
            {message.actions.map((a, idx) => (
              <ActionChip key={idx} action={a} onClick={() => onAction(a)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionChip({ action, onClick }) {
  const Icon =
    action.type === "dossier_card" ? FileText
    : action.type === "prefill_relance" ? Send
    : action.type === "copy" ? CopyIcon
    : ExternalLink;
  const label = action.label || action.type;
  const color =
    action.type === "prefill_relance"
      ? "bg-[#25D366] hover:bg-[#1ebe57] text-white border-transparent"
      : action.type === "dossier_card"
      ? "bg-norea-primary/10 hover:bg-norea-primary/20 text-norea-primary border-norea-primary/30"
      : "bg-white hover:bg-norea-bg text-norea-text border-norea-border";
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-md border transition font-medium ${color}`}
      data-testid={`assistant-action-${action.type}`}
      title={label}
    >
      <Icon className="w-3 h-3" />
      <span className="truncate max-w-[260px]">{label}</span>
    </button>
  );
}
